import { React, useState, useEffect, useRef } from "react";
import { 
  Select, 
  Steps, 
  Button, 
  ConfigProvider, 
  Alert, 
  message,
  Tooltip 
} from "antd";
import {
  UploadOutlined,
  InfoCircleFilled,
  ArrowRightOutlined,
  SettingOutlined,
  CloudOutlined,
  CheckCircleFilled,
  LoadingOutlined,
} from "@ant-design/icons";
import { PlusCircle, DashCircle } from "react-bootstrap-icons";
import { InfoCircleDiv } from "../../components/elements/InfoHoverableDiv";
import { useDirtyState } from "../../hooks/useDirtyState";
import { Form, Input, Upload, Switch } from "antd";
import colors from "../../styles/_colors.scss";
import FetchService from "../../services/fetch.service";

const fetchService = new FetchService();
const { Step } = Steps;

const regionLists = [
  "asia-east1 (Taiwan)",
  "asia-northeast1 (Tokyo)",
  "asia-northeast2 (Osaka)",
  "europe-north1 (Finland)",
  "europe-southwest1 (Madrid)",
  "europe-west1 (Belgium)",
  "europe-west4 (Netherlands)",
  "europe-west8 (Milan)",
  "europe-west9 (Paris)",
  "me-west1 (Tel Aviv)",
  "us-central1 (Iowa)",
  "us-east1 (South Carolina)",
  "us-east4 (Northern Virginia)",
  "us-east5 (Columbus)",
  "us-south1 (Dallas)",
  "us-west1 (Oregon)",
  "africa-south1 (Johannesburg)",
  "asia-east2 (Hong Kong)",
  "asia-northeast3 (Seoul, South Korea)",
  "asia-southeast1 (Singapore)",
  "asia-southeast2 (Jakarta)",
  "asia-south1 (Mumbai, India)",
  "asia-south2 (Delhi, India)",
  "australia-southeast1 (Sydney)",
  "australia-southeast2 (Melbourne)",
  "europe-central2 (Warsaw, Poland)",
  "europe-west10 (Berlin)",
  "europe-west12 (Turin)",
  "europe-west2 (London, UK)",
  "europe-west3 (Frankfurt, Germany)",
  "europe-west6 (Zurich, Switzerland)",
  "me-central1 (Doha)",
  "me-central2 (Dammam)",
  "northamerica-northeast1 (Montreal)",
  "northamerica-northeast2 (Toronto)",
  "southamerica-east1 (Sao Paulo, Brazil)",
  "southamerica-west1 (Santiago, Chile)",
  "us-west2 (Los Angeles)",
  "us-west3 (Salt Lake City)",
  "us-west4 (Las Vegas)",
];

const regionItems = regionLists.map((region) => {
  const key = region.split(" (")[0];
  return key;
});

const infoText = `
    Get detailed error reporting on server-side errors, 
    but this feature is only available for Advanced plans and higher.
    If you're interested in accessing it, please reach out to us, 
    and we'll be happy to assist you with upgrading your plan.
`;

const GoogleCloudConfigForm = (props) => {
  const {
    tagMonitorSubscription,
    saveFunction,
    cloudRunData,
    setModalSaveStatus,
    setConfigParams,
    companyId,
  } = props;

  const [form] = Form.useForm();
  const [configData, setConfigData] = useState(cloudRunData?.config_data);

  const [enableCloudRun, setEnableCloudRun] = useState(
    configData?.current_config?.cloud_run_monitoring || false
  );
  const [serverErrorState, setServerErrorState] = useState(
    configData?.current_config?.ss_error_monitoring || false
  );
  const [inputValue, setInputValue] = useState(
    configData?.current_config?.project_number
  );
  const [resourcesList, setResourcesList] = useState([""]);
  const [isProjectIdFilled, setIsProjectIdFilled] = useState(
    !!configData?.current_config?.project_number?.trim()
  );
  const [isFileUploaded, setIsFileUploaded] = useState(false);
  const [current, setCurrent] = useState(0);

  const initCloudRunState = useRef([]);
  const initServerErrorState = useRef([]);
  const initialResourcesListRef = useRef([]);

  const serverErrorLoggingDisabled =
    tagMonitorSubscription === "essentials";

  // NEW: State for configure button
  const [isConfiguring, setIsConfiguring] = useState(false);
  const [configureResponse, setConfigureResponse] = useState(null);

  // Track if serverside logging has been configured successfully
  const [isServersideLoggingConfigured, setIsServersideLoggingConfigured] = useState(false);

  useEffect(() => setConfigData(cloudRunData?.config_data), [cloudRunData]);

  useEffect(() => {
    if (configData) {
      const initialResources = JSON.parse(
        configData?.current_config?.cloud_run_resources_list?.replace(
          /'/g,
          '"'
        ) || "[]"
      ) || [""];
      setResourcesList(initialResources);

      if (
        !Object.keys(initCloudRunState).find((elem) => elem === "current") ||
        initCloudRunState.current.length === 0
      )
        initCloudRunState.current = enableCloudRun;

      if (
        !Object.keys(initServerErrorState).find((elem) => elem === "current") ||
        initServerErrorState.current.length === 0
      )
        initServerErrorState.current = serverErrorState;

      if (initialResourcesListRef.current.length === 0)
        initialResourcesListRef.current = initialResources;

      const projectId = configData?.current_config?.project_number;
      setIsProjectIdFilled(!!projectId?.trim());

      // Check if serverside logging has been configured (has project_number and resources)
      const hasConfiguration = !!(
        projectId?.trim() &&
        initialResources &&
        initialResources.length > 0 &&
        initialResources.some(r => r.name && r.region)
      );
      setIsServersideLoggingConfigured(hasConfiguration);

      form.setFieldsValue({
        project_number: projectId,
        items: initialResources,
        ss_error_monitoring: configData?.current_config?.ss_error_monitoring,
        cloud_run_monitoring: configData?.current_config?.cloud_run_monitoring,
        upload: [],
      });
    }

    setEnableCloudRun(
      configData?.current_config?.cloud_run_monitoring || false
    );
    setServerErrorState(
      configData?.current_config?.ss_error_monitoring || false
    );
  }, [configData]);

  const isResourceEmpty = (resource) => {
    return (
      (!resource.name || resource.name.trim() === "") &&
      (!resource.region || resource.region.trim() === "")
    );
  };

  const filterEmptyResources = (resources) =>
    resources.filter((resource) => !isResourceEmpty(resource));

  const isDirty = useDirtyState(
    {
      enableCloudRun: initCloudRunState.current,
      serverError: initServerErrorState.current,
      projectNumber: configData?.current_config?.project_number,
      resources: filterEmptyResources(initialResourcesListRef.current),
    },
    {
      enableCloudRun,
      serverError: serverErrorState,
      projectNumber: inputValue,
      resources: filterEmptyResources(resourcesList),
    },
  );

  useEffect(() => {
    // If serverside logging is enabled but not configured, prevent saving
    const cannotSaveDueToMissingConfig =
      serverErrorState === true && !isServersideLoggingConfigured;

    // Only allow saving if there are changes AND (serverside is off OR it's properly configured)
    setModalSaveStatus(isDirty && !cannotSaveDueToMissingConfig);
  }, [isDirty, serverErrorState, isServersideLoggingConfigured, setModalSaveStatus]);

  // Update current step based on state
  useEffect(() => {
    if (!enableCloudRun && !serverErrorState) {
      setCurrent(-1); // No steps visible
    } else if ((enableCloudRun || serverErrorState) && !isProjectIdFilled) {
      setCurrent(0);
    } else if (
      (enableCloudRun || serverErrorState) &&
      isProjectIdFilled &&
      !isFileUploaded
    ) {
      setCurrent(1);
    } else if (serverErrorState && isProjectIdFilled && isFileUploaded) {
      setCurrent(2);
    } else if (
      (enableCloudRun || serverErrorState) &&
      isProjectIdFilled &&
      isFileUploaded
    ) {
      setCurrent(2);
    }
  }, [enableCloudRun, serverErrorState, isProjectIdFilled, isFileUploaded]);

  const onFinish = (values) => {
    if (values["items"]) {
      let trimmedData = values["items"]?.map((item) => {
        return {
          name: item.name.trim(),
          region: item.region.trim(),
        };
      });
      values["items"] = trimmedData;
    }
    saveFunction(values);
  };

  const [showServiceAccountKey, setShowServiceAccountKey] = useState(false);
  const [isWifCredential, setIsWifCredential] = useState(false);

  // ✅ CONFIGURE FUNCTION - Handles both Cloud Run monitoring AND Server-side error logs
  const handleConfigureCloudRun = async () => {
  const values = form.getFieldsValue();
  const fileList = form.getFieldValue('upload');
  
  if (!fileList || fileList.length === 0) {
    message.error("Please upload a service account key first");
    return;
  }

  if (!values.project_number) {
    message.error("Please enter Project Number first");
    return;
  }

  const items = values.items || [];
  const validItems = items.filter(
    (item) => item?.name?.trim() && item?.region?.trim()
  );

  if (validItems.length === 0) {
    message.error("Please add at least one Cloud Run instance");
    return;
  }

  setIsConfiguring(true);
  setConfigureResponse(null);

  try {
    const formDataToSend = new FormData();
    formDataToSend.append("company_id", companyId);
    
    const fileData = fileList[0];
    if (fileData && fileData.originFileObj) {
      const file = fileData.originFileObj      
      formDataToSend.append("file", file);
    }

    let cloudRunResources = [];
    if (values.items && Array.isArray(values.items)) {
      cloudRunResources = values.items.map(item => ({
        name: item.name?.trim() || "",
        region: item.region?.trim() || ""
      })).filter(item => item.name && item.region);
    }

    // ✅ Extract project_id from uploaded JSON file
    const fileData2 = fileList[0];
    const fileText = await fileData2.originFileObj.text();
    const credentials = JSON.parse(fileText);

    const projectId = credentials.type === "external_account"
      ? (values.project_id_wif || "")
      : (credentials.project_id || "");
    const projectNumber = values.project_number;

    const configData = {
      projectid: projectId,
      projectnumber: projectNumber,
      cputhresholdvalue: 0.95,
      memorythresholdvalue: 0.95,
      enable_cloud_run_monitoring: enableCloudRun,
      enable_ss_error_monitoring: serverErrorState,
      items: cloudRunResources,
    };

    formDataToSend.append("config_data", JSON.stringify(configData));
    const response = await fetchService("/configurations/setup_cloud_run/", {
      method: "POST",
      cache: "no-cache",
      mode: "cors",
      body: formDataToSend,
      headers: {},
    });

    const responseData = await response.json();

    if (responseData.success) {
      let successMessage = "Configuration successful!";
      if (enableCloudRun && serverErrorState) {
        successMessage = "Cloud Run Monitoring and Server-side Error Logging configured successfully!";
      } else if (enableCloudRun) {
        successMessage = "Cloud Run Monitoring configured successfully!";
      } else if (serverErrorState) {
        successMessage = "Server-side Error Logging configured successfully!";
      }

      // Mark serverside logging as configured
      setIsServersideLoggingConfigured(true);

      setConfigureResponse({
        success: true,
        message: successMessage,
      });
      message.success(successMessage);
    } else {
      setConfigureResponse({
        success: false,
        error: responseData.error || responseData.details || "Configuration failed",
      });
      message.error(responseData.error || "Configuration failed");
    }
  } catch (error) {
    setConfigureResponse({
      success: false,
      error: `Error: ${error.message}`,
    });
    message.error(`Error: ${error.message}`);
  } finally {
    setIsConfiguring(false);
  }
};

  return (
    <div>
      <ConfigProvider
        theme={{
          components: {
            Steps: {
              colorPrimary: colors.ccDarkBlue,
              processIconColor: colors.ccDarkBlue,
              customIconTop: "50%",
              customIconTransform: "translateY(-50%)",
              finishIconBorderColor: colors.ccDarkBlue,
              colorTextDescription: "rgba(0, 0, 0, 0.88)",
              waitIconColor: "rgba(0, 0, 0, 0.25)",
              waitDotColor: "rgba(0, 0, 0, 0.25)",
            },
          },
        }}
      >
        <Form
          name="GoogleCloudConfigForm"
          onFinish={onFinish}
          layout="vertical"
          form={form}
          initialValues={{
            items: resourcesList,
            ss_error_monitoring:
              configData?.current_config?.ss_error_monitoring,
            cloud_run_monitoring:
              configData?.current_config?.cloud_run_monitoring,
            upload: [],
            remember: true,
          }}
        >
          {/* Configuration Toggles */}
          <div className="switch-container">
            <div className="modal-category fw-bold d-flex justify-content-between align-items-start w-100">
              <div className="switch-text-container">
                <p className="modal-category fw-bold">
                  Cloud Run monitoring
                  <Tooltip title="Track performance of your Cloud Run instances">
                    <InfoCircleFilled
                      size={15}
                      className="info-icon"
                      style={{ marginLeft: "10px" }}
                    />
                  </Tooltip>
                </p>
              </div>
              <Form.Item
                name="cloud_run_monitoring"
                valuePropName="checked"
                style={{ marginLeft: "auto", marginBottom: "1rem" }}
              >
                <Switch
                  onChange={(value) => {
                    setEnableCloudRun(value);
                    // Update config params for save
                    setConfigParams((prev) => ({
                      ...prev,
                      cloud_run_monitoring: value,
                    }));
                    // When disabling Cloud Run, also disable Server-side error logs
                    if (!value) {
                      setServerErrorState(false);
                      form.setFieldValue("ss_error_monitoring", false);
                      setConfigParams((prev) => ({
                        ...prev,
                        cloud_run_monitoring: value,
                        ss_error_monitoring: false,
                      }));
                    }
                  }}
                />
              </Form.Item>
              <p
                className="ps-2"
                style={{ lineHeight: "34px", display: "inline-block" }}
              >
                {enableCloudRun ? " " : " "}
              </p>
            </div>
            <div className="modal-category fw-bold d-flex justify-content-between align-items-start w-100">
              <div className="switch-text-container">
                <p className="modal-category fw-bold">
                  Server-side error logs
                  {serverErrorLoggingDisabled ? (
                    <Tooltip title={infoText}>
                      <InfoCircleFilled
                        size={15}
                        className="info-icon"
                        style={{ marginLeft: "10px" }}
                      />
                    </Tooltip>
                  ) : (
                    <Tooltip title="Get detailed error reporting on server-side errors">
                      <InfoCircleFilled
                        size={15}
                        className="info-icon"
                        style={{ marginLeft: "10px" }}
                      />
                    </Tooltip>
                  )}
                </p>
              </div>

              <Form.Item
                valuePropName="checked"
                name="ss_error_monitoring"
                style={{ marginLeft: "auto", marginBottom: "2.5rem" }}
              >
                <Switch
                  onChange={(value) => {
                    setServerErrorState(value);
                    // Update config params for save
                    setConfigParams((prev) => ({
                      ...prev,
                      ss_error_monitoring: value,
                    }));
                    // When enabling server-side error logs, also enable Cloud Run monitoring
                    if (value) {
                      setEnableCloudRun(true);
                      form.setFieldValue("cloud_run_monitoring", true);
                      setConfigParams((prev) => ({
                        ...prev,
                        cloud_run_monitoring: true,
                        ss_error_monitoring: value,
                      }));
                    }
                    // When disabling, configuration is preserved but disabled
                    // User can re-enable later without needing to reconfigure
                  }}
                  disabled={
                    serverErrorLoggingDisabled  // Only disabled for essentials plan
                  }
                />
              </Form.Item>
              <p
                className="ps-2"
                style={{ lineHeight: "34px", display: "inline-block" }}
              >
                {serverErrorState ? " " : " "}
              </p>
            </div>
          </div>

          {/* Steps - show when at least one monitoring feature is enabled */}
          {(enableCloudRun || serverErrorState) && (
            <Steps
              current={current}
              direction="vertical"
              className="centered-step-icons"
            >
              {/* STEP 1: Project ID */}
              <Step
                description={
                  <div className="steps-description-container">
                    <p
                      className="modal-category fw-bold"
                      style={{ marginBottom: "1rem" }}
                    >
                      Project number
                    </p>
                    <Form.Item
                      name="project_number"
                      rules={[
                        {
                          required: true,
                          message: "Please add your project number!",
                        },
                      ]}
                    >
                      <Input
                        style={{ width: "17rem" }}
                        placeholder="485687865"
                        value={inputValue}
                        onChange={(e) => {
                          const value = e.target.value;
                          setInputValue(value);
                          setIsProjectIdFilled(!!value?.trim());
                        }}
                        disabled={!(enableCloudRun || serverErrorState)}
                      />
                    </Form.Item>
                  </div>
                }
              />

              {/* STEP 2: Service Account */}
              <Step
                description={
                  <div
                    className={`steps-description-container ${
                      !isProjectIdFilled ? "disabled-div" : ""
                    }`}
                  >
                    <div>
                      <p
                        className="modal-category fw-bold"
                        style={{ marginBottom: 6 }}
                      >
                        Service account key
                        <Tooltip title="You can find service account keys in the IAM & Admin section of the Google Cloud Console.">
                          <InfoCircleFilled
                            className="info-icon"
                            style={{ marginLeft: "10px" }}
                          />
                        </Tooltip>
                      </p>
                    </div>
                    <p>
                      Create a service account in the GCP project and upload the
                      JSON file below.{" "}
                      <a
                        style={{ color: "#000" }}
                        target="_blank"
                        href="https://docs.code-cube.io/tag-monitor/configuration/monitor-cloud-run#block-203f9a0175df80718e17fe0834171fc5"
                      >
                        →
                      </a>
                    </p>
                    <div
                      style={{
                        display: "flex",
                        flexDirection: "column",
                        gap: "4px",
                      }}
                    >
                      <Form.Item
                        name="upload"
                        valuePropName="fileList"
                        getValueFromEvent={(e) => {
                          if (Array.isArray(e)) {
                            setIsFileUploaded(e.length > 0);
                            return e;
                          }
                          const fileList = e && e.fileList;
                          setIsFileUploaded(fileList && fileList.length > 0);
                          return fileList;
                        }}
                        rules={[
                          {
                            required: true,
                            message: "Please upload a file!",
                          },
                        ]}
                      >
                        <Upload
                          accept=".json"
                          name="logo"
                          action="/upload.do"
                          listType="text"
                          type="default"
                          beforeUpload={() => false}
                          disabled={!isProjectIdFilled}
                          onChange={async (info) => {
                            const file = info.fileList[0]?.originFileObj;
                            if (file) {
                              try {
                                const text = await file.text();
                                const creds = JSON.parse(text);
                                setIsWifCredential(creds.type === "external_account");
                              } catch {
                                setIsWifCredential(false);
                              }
                            } else {
                              setIsWifCredential(false);
                            }
                          }}
                        >
                          <div
                            style={{
                              display: "flex",
                              flexDirection: "column",
                              alignItems: "flex-start",
                            }}
                          >
                            <div
                              className="btn cc-btn-outline-lime fw-bold"
                              style={{
                                marginBottom: 0,
                                opacity: isProjectIdFilled ? 1 : 0.3,
                                filter: isProjectIdFilled
                                  ? "none"
                                  : "drop-shadow(0 0 3px rgba(0,0,0,0.3))",
                              }}
                            >
                              Upload service account key
                              <img
                                src={require("../../assets/Upload.svg").default}
                                alt="Upload Icon"
                                style={{
                                  paddingLeft: ".8rem",
                                  paddingBottom: ".3rem",
                                }}
                              />
                            </div>
                          </div>
                        </Upload>
                      </Form.Item>
                    {isWifCredential && (
                      <Form.Item
                        name="project_id_wif"
                        label="Project ID"
                        rules={[{ required: true, message: "Required for Workload Identity credentials" }]}
                        style={{ marginTop: "0.75rem" }}
                      >
                        <Input
                          style={{ width: "17rem" }}
                          placeholder="my-gcp-project-id"
                        />
                      </Form.Item>
                    )}
                    </div>
                  </div>
                }
              />

              {/* STEP 3: Cloud Run Instances - ALWAYS SHOWN when either feature is enabled */}
              <Step
                description={
                  <div
                    className={`steps-description-container ${
                      !isFileUploaded ? "disabled-div" : ""
                    }`}
                  >
                    <p
                      className="modal-category fw-bold"
                      style={{ marginBottom: 6 }}
                    >
                      Configure Cloud Run instances to monitor
                    </p>
                    <p
                      style={{ marginBottom: 6 }}
                    >
                      Add the Cloud Run service name(s) and region(s).{" "}
                    </p>

                    {/* ✅ DynamicInputField - Shows for BOTH Cloud Run and Server-side */}
                    <DynamicInputField
                      setResourcesList={setResourcesList}
                      resourcesList={resourcesList}
                      isFileUploaded={isFileUploaded}
                    />

                    <div>
                      <Form.Item>
                        <Button
                          type="default"
                          className="btn btn-sm fw-bold mt-3"
                          onClick={handleConfigureCloudRun}
                          disabled={!isFileUploaded || isConfiguring}
                          loading={isConfiguring}
                        >
                          {isConfiguring ? "Configuring..." : "Start configuration"}{" "}
                          <ArrowRightOutlined />
                        </Button>
                      </Form.Item>

                      {/* Show response */}
                      {configureResponse && (
                        <Alert
                          message={configureResponse.success ? "Success" : "Error"}
                          description={
                            configureResponse.success
                              ? configureResponse.message
                              : configureResponse.error
                          }
                          type={configureResponse.success ? "success" : "error"}
                          showIcon
                          style={{ marginTop: "1rem" }}
                        />
                      )}
                    </div>
                  </div>
                }
              />
            </Steps>
          )}
        </Form>
      </ConfigProvider>
    </div>
  );
};

// ✅ DynamicInputField - Exact copy from your working version
const DynamicInputField = (props) => {
  const { resourcesList, setResourcesList, isFileUploaded } = props;

  const handleAdd = (add, fields) => {
    add();
    const newList = [...resourcesList, { name: "", region: "" }];
    setResourcesList(newList);
  };

  const handleRemove = (name, remove, fields) => {
    remove(name);
    const newList = resourcesList.filter((_, index) => index !== name);
    setResourcesList(newList);
  };

  const handleFieldChange = (index, field, value, fields) => {
    const updatedList = [...resourcesList];
    updatedList[index] = {
      ...updatedList[index],
      [field]: value,
    };
    setResourcesList(updatedList);
  };

  return (
    <div className="list-col-2-add">
      <Form.List
        name="items"
        rules={[
          {
            validator: async (_, names) => {
              if (!names || names.length < 1) {
                return Promise.reject(
                  new Error("At least 1 resource name is required")
                );
              }
            },
          },
        ]}
      >
        {(fields, { add, remove }, { errors }) => (
          <>
            {fields.length === 0 && (
              <div
                className="inputs-row-2-col-single"
                style={{
                  padding: 0,
                  opacity: isFileUploaded ? 1 : 0.5,
                  pointerEvents: isFileUploaded ? "auto" : "none",
                }}
              >
                <div>
                  <p
                    className="modal-category fw-bold"
                    style={{ marginBottom: 6 }}
                  >
                    Instance name
                  </p>
                  <Form.Item
                    name={[0, "name"]}
                    fieldKey={[0, "name"]}
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: "Please input a resource name",
                      },
                    ]}
                    style={{ marginBottom: 0, minWidth: "17rem" }}
                  >
                    <Input
                      placeholder="Resource name"
                      disabled={!isFileUploaded}
                    />
                  </Form.Item>
                </div>

                <div>
                  <p
                    className="modal-category fw-bold"
                    style={{ marginBottom: 6 }}
                  >
                    Region
                  </p>
                  <Form.Item
                    name={[0, "region"]}
                    fieldKey={[0, "region"]}
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: "Please select a region",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      placeholder="Select the resource region"
                      style={{ minWidth: "14rem" }}
                      showSearch
                      disabled={!isFileUploaded}
                    >
                      {regionItems.map((item, index) => (
                        <Select.Option key={`${item}-${index}`} value={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>

                <div
                  className="inputs-row-2-col-single-actions"
                  style={{ alignItems: "center" }}
                >
                  {/* Add button for the first field */}
                  <PlusCircle
                    className="color-dark ms-2"
                    size={18}
                    onClick={() => isFileUploaded && handleAdd(add)}
                    style={{
                      opacity: isFileUploaded ? 1 : 0.3,
                      cursor: isFileUploaded ? "pointer" : "not-allowed",
                    }}
                  />
                </div>
              </div>
            )}
            {fields.map(({ key, name, ...field }, index) => (
              <div
                key={`${field.key}-${index}`}
                className="inputs-row-2-col-single"
                style={{
                  padding: 0,
                  opacity: isFileUploaded ? 1 : 0.5,
                  pointerEvents: isFileUploaded ? "auto" : "none",
                }}
              >
                <div>
                  <p
                    className="modal-category fw-bold"
                    style={{ marginBottom: 6 }}
                  >
                    Instance name
                  </p>
                  <Form.Item
                    {...field}
                    name={[name, "name"]}
                    fieldKey={[key, "name"]}
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: "Please input a resource name",
                      },
                    ]}
                    style={{ marginBottom: 0, minWidth: "17rem" }}
                  >
                    <Input
                      placeholder="Resource name"
                      disabled={!isFileUploaded}
                      onChange={(e) =>
                        isFileUploaded &&
                        handleFieldChange(name, "name", e.target.value, fields)
                      }
                    />
                  </Form.Item>
                </div>

                <div style={{ flex: "1" }}>
                  <p
                    className="modal-category fw-bold"
                    style={{ marginBottom: 6 }}
                  >
                    Region
                  </p>
                  <Form.Item
                    {...field}
                    name={[name, "region"]}
                    fieldKey={[key, "region"]}
                    validateTrigger={["onChange", "onBlur"]}
                    rules={[
                      {
                        required: true,
                        whitespace: true,
                        message: "Please select a region",
                      },
                    ]}
                    style={{ marginBottom: 0 }}
                  >
                    <Select
                      placeholder="Select the resource region"
                      style={{ minWidth: "14rem" }}
                      showSearch
                      disabled={!isFileUploaded}
                      onChange={(value) =>
                        isFileUploaded &&
                        handleFieldChange(name, "region", value, fields)
                      }
                    >
                      {regionItems.map((item, index) => (
                        <Select.Option key={`${item}-${index}`} value={item}>
                          {item}
                        </Select.Option>
                      ))}
                    </Select>
                  </Form.Item>
                </div>
                <div className="inputs-row-2-col-single-actions">
                  <DashCircle
                    className="color-dark"
                    size={18}
                    onClick={() =>
                      isFileUploaded && handleRemove(name, remove, fields)
                    }
                    style={{
                      opacity: isFileUploaded ? 1 : 0.3,
                      cursor: isFileUploaded ? "pointer" : "not-allowed",
                    }}
                  />
                  {index === fields.length - 1 && (
                    <PlusCircle
                      className="color-dark"
                      size={18}
                      onClick={() => isFileUploaded && handleAdd(add, fields)}
                      style={{
                        opacity: isFileUploaded ? 1 : 0.3,
                        cursor: isFileUploaded ? "pointer" : "not-allowed",
                      }}
                    />
                  )}
                </div>
              </div>
            ))}

            {/* <Form.Item> */}
            <Form.ErrorList errors={errors} />
            {/* </Form.Item> */}
          </>
        )}
      </Form.List>
    </div>
  );
};

export default GoogleCloudConfigForm;
